import { patch } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { useCurrentUserId } from "../../lib/useCurrentUserId";
import {
  type Case,
  caseResponseSchema,
  CaseStatus,
  type UpdateCaseRequest,
  updateCaseRequestSchema,
} from "../types";
import { getAgencyCasesQueryKey } from "./useAgencyCases";
import { getCaseQueryKey } from "./useCase";
import { getCaseVisitsQueryKey } from "./useCaseVisits";

const CASE_URL = `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/:workplaceId/cases/:caseId`;

export function useUpdateCase(
  caseId: string,
  options: UseMutationOptions<Case, string, UpdateCaseRequest> = {}
) {
  const queryClient = useQueryClient();
  const workplaceId = useCurrentUserId() ?? "";

  return useMutation({
    mutationFn: async (params) => {
      const response = await patch({
        url: CASE_URL.replace(":workplaceId", workplaceId).replace(":caseId", caseId),
        data: params,
        requestSchema: updateCaseRequestSchema,
        responseSchema: caseResponseSchema,
      });
      return response.data.data;
    },
    onSuccess: (caseItem) => {
      void queryClient.invalidateQueries(
        getAgencyCasesQueryKey({
          workplaceId,
          filter: { status: CaseStatus.OPEN },
        })
      );
      void queryClient.invalidateQueries(
        getAgencyCasesQueryKey({
          workplaceId,
          filter: { status: CaseStatus.CLOSED },
        })
      );
      queryClient.setQueryData(getCaseQueryKey({ workplaceId, caseId: caseItem.id }), caseItem);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries(getCaseQueryKey({ workplaceId, caseId: caseItem.id }));
      void queryClient.invalidateQueries(
        getCaseVisitsQueryKey({ workplaceId, caseId: caseItem.id })
      );
    },
    ...options,
  });
}
